import React, { useEffect, useMemo } from "react";
import Box from "@amzn/meridian/box";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Heading from "@amzn/meridian/heading";

import { useAppDispatch, useAppSelector } from "src/store";
import { selectStationCode, setStationCode } from "src/store/slices/shiftPlanSlice";
import { showError } from "src/store/slices/errorSlice";
import { getUserName } from "src/services/boson/bosonClient";
import {
  fetchSiteLayoutConfig,
  fetchAvailableProcessPaths,
  saveSiteLayoutConfig,
  selectSiteLayoutState,
  selectProcessPaths,
  selectAvailableProcessPaths,
  selectHasUnsavedChanges,
  addProcessPath,
} from "src/store/slices/siteLayoutSlice";
import { ProcessPathConfig, ValidationErrors } from "./siteLayout/types";
import { validateSiteLayout } from "./siteLayout/utils/validation";
import AddProcessPathButton from "./siteLayout/components/AddProcessPathButton";
import ProcessPathCardList from "./siteLayout/components/ProcessPathCardList";
import SiteLayoutHeader from "./siteLayout/components/SiteLayoutHeader";

const SiteLayoutTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const stationCode = useAppSelector(selectStationCode);
  const { loadStatus, availableProcessPathsStatus, saveStatus, errorMessage, lastModifiedBy, lastModifiedAt } = useAppSelector(selectSiteLayoutState);
  const processPaths = useAppSelector(selectProcessPaths);
  const availableProcessPaths = useAppSelector(selectAvailableProcessPaths);
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);

  useEffect(() => {
    if (!stationCode) {
      const searchParams = new URLSearchParams(window.location.search);
      const stationCodeParam = searchParams.get('stationCode');
      if (stationCodeParam) {
        dispatch(setStationCode({ stationCode: stationCodeParam }));
      } else {
        dispatch(showError({
          title: "Warehouse ID Required",
          message: "Please select a valid warehouse ID from the dropdown",
        }));
      }
    } else {
      dispatch(fetchSiteLayoutConfig(stationCode));
      dispatch(fetchAvailableProcessPaths(stationCode));
    }
  }, [dispatch, stationCode]);

  const existingProcessPathKeys = processPaths.map(
    (pp) => `${pp.processId}-${pp.functionId}`
  );

  const validationErrors: ValidationErrors = useMemo(
    () => validateSiteLayout(processPaths),
    [processPaths]
  );

  const handleAddProcessPath = (config: ProcessPathConfig) => {
    dispatch(addProcessPath(config));
  };

  const handleSave = async () => {
    if (!stationCode) return;

    let alias: string;
    try {
      alias = await getUserName();
    } catch (err: any) {
      dispatch(showError({
        title: 'Unable to Determine Current User',
        message: err?.message ?? 'Unable to determine the current operator. Please sign in again.',
      }));
      return;
    }

    dispatch(saveSiteLayoutConfig({
      warehouseId: stationCode,
      requestedBy: alias,
      processPaths: processPaths.map((pp) => ({
        processId: pp.processId,
        processPathName: pp.processPathName,
        departmentName: pp.departmentName,
        functionId: pp.functionId,
        functionName: pp.functionName,
        functionType: pp.functionType,
        staffingMode: pp.staffingMode,
        lines: pp.staffingMode === 'PROCESS_LINES'
          ? pp.lines.map((line) => ({
              // TODO: Remove lineId = lineName mapping once got conformation do we need id
              lineId: line.lineName || line.lineId,
              lineName: line.lineName,
              lineCapacity: line.workstations.length,
              workstations: line.workstations.map((ws) => ({
                workstationId: ws.workstationId,
                enabled: ws.enabled,
              })),
            }))
          : [],
        individualWorkstations: pp.staffingMode === 'DEPARTMENT'
          ? pp.individualWorkstations.map((ws) => ({
              workstationId: ws.workstationId,
              enabled: ws.enabled,
            }))
          : undefined,
      })),
    }));
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const saveDisabled = !hasUnsavedChanges || hasValidationErrors;

  if (loadStatus === "idle" || loadStatus === "loading") {
    return (
      <Box spacingInset="400">
        <Text color="secondary">Loading site layout...</Text>
      </Box>
    );
  }

  if (loadStatus === "error") {
    return (
      <Box spacingInset="400">
        <Alert type="error">
          {errorMessage || "Failed to load site layout configuration."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box spacingInset="400">
      <Heading level={3} type="h300">Site Layout</Heading>

      {availableProcessPathsStatus === 'error' && (
        <Box spacingInset="300 none">
          <Alert type="warning">
            Failed to load available process paths. You can still edit existing configuration.
          </Alert>
        </Box>
      )}

      <Box spacingInset="300 none">
        <AddProcessPathButton
          availableProcessPaths={availableProcessPaths}
          existingProcessPathKeys={existingProcessPathKeys}
          onAdd={handleAddProcessPath}
          disabled={loadStatus !== "success" || availableProcessPathsStatus !== "success"}
        />
      </Box>

      {saveStatus === 'error' && errorMessage && (
        <Box spacingInset="300 none">
          <Alert type="error">{errorMessage}</Alert>
        </Box>
      )}

      {hasValidationErrors && hasUnsavedChanges && (
        <Box spacingInset="300 none">
          <Alert type="warning">
            Please fix the highlighted errors before saving.
          </Alert>
        </Box>
      )}

      {processPaths.length === 0 ? (
        <Box spacingInset="300 none">
          <Text color="secondary">
            No process paths configured. Add a process path to get started.
          </Text>
        </Box>
      ) : (
        <ProcessPathCardList
          processPaths={processPaths}
        />
      )}

      <Box spacingInset="400 none">
        <SiteLayoutHeader
          lastModifiedBy={lastModifiedBy}
          lastModifiedAt={lastModifiedAt}
          onSave={handleSave}
          saveDisabled={saveDisabled}
          saving={saveStatus === 'saving'}
        />
      </Box>
    </Box>
  );
};

export default SiteLayoutTab;

import './SiteLayoutTab.scss';